/**
 * VirtualizedPropertyTable Template
 *
 * Base template for virtualized table with property data display
 * Uses @tanstack/react-virtual for performance optimization
 * Supports bulk actions with checkbox selection
 *
 * @template T - The property data type
 */

"use client";

import React, { useMemo } from "react";

import { useVirtualizer } from "@tanstack/react-virtual";

import { cn } from "@/utils/cn";

import { PropertyCheckbox } from "./PropertyCheckbox";
import styles from "./VirtualizedPropertyTable.module.scss";

export interface PropertyTableColumn<T = any> {
  key: keyof T | "actions" | "select";
  label: string;
  sortable?: boolean;
  width?: string | number;
  align?: "start" | "center" | "end";
  render?: (value: any, item: T, index: number) => React.ReactNode;
}

export interface VirtualizedPropertyTableProps<T = any> {
  /** Array of data items to display */
  data: T[];
  /** Column configuration */
  columns: PropertyTableColumn<T>[];
  /** Selected item IDs */
  selectedKeys: Set<string>;
  /** Selection change handler */
  onSelectionChange: (keys: Set<string>) => void;
  /** Item height for virtualization (default: 60) */
  itemHeight?: number;
  /** Container height (default: 400) */
  height?: number;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state component */
  emptyContent?: React.ReactNode;
  /** Row key extractor */
  getRowKey: (item: T) => string;
  /** Row click handler */
  onRowClick?: (item: T, index: number) => void;
  /** Custom row className */
  getRowClassName?: (item: T, index: number) => string;
  /** Enable bulk actions */
  enableBulkActions?: boolean;
  /** Custom cell renderers */
  cellRenderers?: Record<
    string,
    (value: any, item: T, index: number) => React.ReactNode
  >;
  /** Table container className */
  className?: string;
  /** Accessibility label */
  "aria-label"?: string;
  /** Enable virtualization (absolute rows). When false, render regular table rows. */
  virtualized?: boolean;
}

export function VirtualizedPropertyTable<T = any>({
  data,
  columns,
  selectedKeys,
  onSelectionChange,
  itemHeight = 60,
  height = 400,
  isLoading = false,
  emptyContent,
  getRowKey,
  onRowClick,
  getRowClassName,
  enableBulkActions = true,
  cellRenderers = {},
  className,
  "aria-label": ariaLabel = "Property table",
  virtualized = true,
}: VirtualizedPropertyTableProps<T>) {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);

  // Virtualization setup
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: 5,
  });

  // Synchronize horizontal scroll between header and body containers
  React.useEffect(() => {
    const headerEl = headerRef.current;
    const bodyEl = parentRef.current;
    if (!headerEl || !bodyEl) return;

    let syncing = false;
    const onBody = () => {
      if (syncing) return;
      syncing = true;
      headerEl.scrollLeft = bodyEl.scrollLeft;
      syncing = false;
    };
    const onHeader = () => {
      if (syncing) return;
      syncing = true;
      bodyEl.scrollLeft = headerEl.scrollLeft;
      syncing = false;
    };

    bodyEl.addEventListener("scroll", onBody, { passive: true });
    headerEl.addEventListener("scroll", onHeader, { passive: true });
    return () => {
      bodyEl.removeEventListener("scroll", onBody as any);
      headerEl.removeEventListener("scroll", onHeader as any);
    };
  }, [data.length]);

  // Enhanced columns with selection column
  const enhancedColumns = useMemo(() => {
    const cols = [...columns];

    if (enableBulkActions && !cols.find((col) => col.key === "select")) {
      cols.unshift({
        key: "select",
        label: "",
        width: 48,
        align: "center",
      });
    }

    return cols;
  }, [columns, enableBulkActions]);

  // Normalized column widths for <colgroup> to keep header/body aligned
  const normalizedWidths = useMemo(
    () =>
      enhancedColumns.map((c) => {
        if (typeof c.width === "number") return `${c.width}px`;
        if (typeof c.width === "string") return c.width;
        return "auto";
      }),
    [enhancedColumns],
  );

  // Handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allKeys = new Set(data.map(getRowKey));
      onSelectionChange(allKeys);
    } else {
      onSelectionChange(new Set());
    }
  };

  const handleSelectItem = (itemKey: string, checked: boolean) => {
    const newSelection = new Set(selectedKeys);

    if (checked) {
      newSelection.add(itemKey);
    } else {
      newSelection.delete(itemKey);
    }

    onSelectionChange(newSelection);
  };

  const isAllSelected = data.length > 0 && selectedKeys.size === data.length;
  const isIndeterminate =
    selectedKeys.size > 0 && selectedKeys.size < data.length;

  // Helper function to get nested property values
  const getNestedValue = (obj: any, path: string): any => {
    if (!obj || typeof obj !== "object") return null;

    const keys = path.split(".");
    let current = obj;

    for (const key of keys) {
      if (current == null || typeof current !== "object") {
        return null;
      }
      current = current[key];
    }

    return current;
  };

  // Render cell content
  const renderCell = (
    column: PropertyTableColumn<T>,
    item: T,
    index: number,
  ) => {
    const { key, render } = column;

    // Selection column
    if (key === "select") {
      const itemKey = getRowKey(item);
      return (
        <PropertyCheckbox
          isSelected={selectedKeys.has(itemKey)}
          onSelectionChange={(checked) => handleSelectItem(itemKey, checked)}
          aria-label={`Select row ${index + 1}`}
          className={cn(styles.figmaCheckbox, {
            [styles.checked]: selectedKeys.has(itemKey),
          })}
        />
      );
    }

    // Custom renderer
    if (render) {
      const keyStr = String(key);
      const value = keyStr.includes(".")
        ? getNestedValue(item, keyStr)
        : item[key as keyof T];
      return render(value, item, index);
    }

    // Cell renderer from props
    if (cellRenderers[key as string]) {
      const keyStr = String(key);
      const value = keyStr.includes(".")
        ? getNestedValue(item, keyStr)
        : item[key as keyof T];
      return cellRenderers[key as string](value, item, index);
    }

    // Default rendering with nested property support
    const keyStr = String(key);
    const value = keyStr.includes(".")
      ? getNestedValue(item, keyStr)
      : item[key as keyof T];
    return value != null ? String(value) : "";
  };

  if (isLoading) {
    return (
      <div className={cn(styles.loadingState, className)}>
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn(styles.emptyState, className)}>
        {emptyContent || (
          <div>
            <div className={styles.emptyStateTitle}>No data available</div>
            <div className={styles.emptyStateDescription}>
              Try adjusting your search criteria
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(styles.virtualizedTable, className)}
      aria-label={ariaLabel}
    >
      {/* Header Table - Static (borders/rounded come from SCSS on root) */}
      <div
        ref={headerRef}
        className={cn(
          "overflow-x-auto overflow-y-hidden",
          styles.figmaHeaderContainer,
        )}
      >
        <table className={cn(styles.figmaHeaderTable)}>
          <colgroup>
            {normalizedWidths.map((w, idx) => (
              <col
                key={idx}
                style={{ width: w }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {enhancedColumns.map((column) => (
                <th
                  key={column.key as string}
                  className={cn(
                    "",
                    column.align === "center" && "text-center",
                    column.align === "end" && "text-right",
                    column.key === "select" &&
                      cn("w-12 min-w-12 max-w-12", styles.selectionColumn),
                    column.key === "customId" && styles.idColumn,
                    column.key === "photo" && styles.photoColumn,
                    column.key === "address" && styles.addressColumn,
                    column.key === "price" && styles.priceColumn,
                    ["area", "rooms", "floor"].includes(column.key as string) &&
                      styles.numericColumn,
                    ["updated", "lastCall"].includes(column.key as string) &&
                      styles.dateColumn,
                    column.key === "exclusive" && styles.statusColumn,
                    column.key === "actions" && styles.actionsColumn,
                  )}
                >
                  {column.key === "select" && enableBulkActions ? (
                    <PropertyCheckbox
                      isSelected={isAllSelected}
                      isIndeterminate={isIndeterminate}
                      onSelectionChange={handleSelectAll}
                      aria-label="Select all rows"
                      className={styles.figmaCheckbox}
                    />
                  ) : (
                    <span
                      className={styles.headerText}
                      title={String(column.label)}
                    >
                      {column.label}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>

      {/* Body */}
      <div
        className={cn("overflow-hidden", styles.figmaBodyContainer)}
        style={{ height }}
      >
        {virtualized ? (
          <div
            ref={parentRef}
            className="overflow-auto h-full"
            style={{ height }}
          >
            <div
              style={{
                height: virtualizer.getTotalSize(),
                width: "100%",
                position: "relative",
              }}
            >
              <table className={cn(styles.figmaBodyTable)}>
                <colgroup>
                  {normalizedWidths.map((w, idx) => (
                    <col
                      key={idx}
                      style={{ width: w }}
                    />
                  ))}
                </colgroup>
                <tbody>
                  {virtualizer.getVirtualItems().map((virtualRow) => {
                    const item = data[virtualRow.index];
                    const itemKey = getRowKey(item);
                    const rowStyle: React.CSSProperties = {
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: itemHeight,
                      transform: `translateY(${virtualRow.start}px)`,
                    };
                    return (
                      <tr
                        key={itemKey}
                        style={rowStyle}
                        className={cn(
                          styles.tableRow,
                          selectedKeys.has(itemKey) && styles.tableRowSelected,
                          getRowClassName?.(item, virtualRow.index),
                        )}
                        onClick={() => onRowClick?.(item, virtualRow.index)}
                      >
                        {enhancedColumns.map((column) => (
                          <td
                            key={column.key as string}
                            className={cn(
                              column.align === "center" && "text-center",
                              column.align === "end" && "text-right",
                              column.key === "select" &&
                                cn(
                                  "w-12 min-w-12 max-w-12",
                                  styles.selectionColumn,
                                ),
                              column.key === "customId" && styles.idColumn,
                              column.key === "photo" && styles.photoColumn,
                              column.key === "address" && styles.addressColumn,
                              column.key === "price" && styles.priceColumn,
                              ["area", "rooms", "floor"].includes(
                                column.key as string,
                              ) && styles.numericColumn,
                              ["updated", "lastCall"].includes(
                                column.key as string,
                              ) && styles.dateColumn,
                              column.key === "exclusive" && styles.statusColumn,
                              column.key === "actions" && styles.actionsColumn,
                            )}
                          >
                            {renderCell(column, item, virtualRow.index)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div
            ref={parentRef}
            className="overflow-auto h-full"
            style={{ height }}
          >
            <table className={cn(styles.figmaBodyTable)}>
              <colgroup>
                {normalizedWidths.map((w, idx) => (
                  <col
                    key={idx}
                    style={{ width: w }}
                  />
                ))}
              </colgroup>
              <tbody>
                {data.map((item, index) => {
                  const itemKey = getRowKey(item);
                  return (
                    <tr
                      key={itemKey}
                      className={cn(
                        styles.tableRow,
                        selectedKeys.has(itemKey) && styles.tableRowSelected,
                        getRowClassName?.(item, index),
                      )}
                      onClick={() => onRowClick?.(item, index)}
                    >
                      {enhancedColumns.map((column) => (
                        <td
                          key={column.key as string}
                          className={cn(
                            column.align === "center" && "text-center",
                            column.align === "end" && "text-right",
                            column.key === "select" &&
                              cn(
                                "w-12 min-w-12 max-w-12",
                                styles.selectionColumn,
                              ),
                            column.key === "customId" && styles.idColumn,
                            column.key === "photo" && styles.photoColumn,
                            column.key === "address" && styles.addressColumn,
                            column.key === "price" && styles.priceColumn,
                            ["area", "rooms", "floor"].includes(
                              column.key as string,
                            ) && styles.numericColumn,
                            ["updated", "lastCall"].includes(
                              column.key as string,
                            ) && styles.dateColumn,
                            column.key === "exclusive" && styles.statusColumn,
                            column.key === "actions" && styles.actionsColumn,
                          )}
                        >
                          {renderCell(column, item, index)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default VirtualizedPropertyTable;
